import React, { useState, useEffect, CSSProperties } from 'react';

interface ImageUploadProps {
  onChange: (file: File | null) => void;
  label?: string;
  rules?: {
    required?: boolean | string;
    validate?: (file: File | null) => boolean | string;
  };
  className?: string;
  style?: CSSProperties;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onChange,
  label = "Click to upload or drag and drop",
  rules = {},
  className = "",
  style = {},
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validate = (file: File | null): string | null => {
    if (rules.required && !file) {
      return typeof rules.required === "string" ? rules.required : "Image is required";
    }
    if (rules.validate) {
      const result = rules.validate(file);
      if (result !== true) {
        return typeof result === "string" ? result : "Invalid image file";
      }
    }
    return null;
  };

  useEffect(() => {
    setError(validate(file));
  }, [file]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    if (selected) {
      setPreview(URL.createObjectURL(selected));
    } else {
      setPreview(null);
    }
    onChange(selected);
  };

  return (
    <div className={`w-full ${className}`} style={style}>
      <label
        htmlFor="dropzone-file"
        className={`flex flex-col items-center justify-center w-full h-64 rounded-lg cursor-pointer 
          bg-[#F2F2F2] text-gray-600 dark:text-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600`}
      >
        <div className="flex flex-col items-center justify-center pt-5 pb-6">
          {!preview ? (
            <>
              <svg
                className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 16"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 
                     5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 
                     4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                />
              </svg>
              <p className="mb-2 text-sm text-center">
                <span className="font-semibold">{label}</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                SVG, PNG, JPG or GIF (MAX. 800x400px)
              </p>
            </>
          ) : (
            <img
              src={preview}
              alt="Preview"
              className="h-48 object-contain"
            />
          )}
        </div>
        <input
          id="dropzone-file"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </label>
      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-500">{error}</p>
      )}
    </div>
  );
};

export default ImageUpload;

